import type { ReactNode } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Info,
  XCircle,
} from 'lucide-react'
import './MimUi.css'

type Tone = 'blue' | 'gold' | 'green' | 'red' | 'orange' | 'grey'

export function MimPage({
  title,
  subtitle,
  actions,
  children,
  compactHeader = false,
}: {
  title: string
  subtitle?: string
  actions?: ReactNode
  children: ReactNode
  /** Single-row header: subtitle inline with title instead of stacked. */
  compactHeader?: boolean
}) {
  return (
    <section className={`mim-page${compactHeader ? ' mim-page-compact' : ''}`}>
      <header className={`mim-page-header${compactHeader ? ' mim-page-header-compact' : ''}`}>
        <div className={compactHeader ? 'mim-page-title-row' : undefined}>
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {actions && <div className="mim-page-actions">{actions}</div>}
      </header>
      {children}
    </section>
  )
}

export function MimCard({
  title,
  subtitle,
  actions,
  children,
  className = '',
}: {
  title?: string
  subtitle?: string
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={`mim-card ${className}`}>
      {(title || subtitle || actions) && (
        <header className="mim-card-header">
          <div>
            {title && <h2>{title}</h2>}
            {subtitle && <p>{subtitle}</p>}
          </div>
          {actions && <div className="mim-card-actions">{actions}</div>}
        </header>
      )}
      <div className="mim-card-body">{children}</div>
    </section>
  )
}

export function KpiCard({
  label,
  value,
  detail,
  tone = 'blue',
  icon,
  compact = false,
}: {
  label: string
  value: ReactNode
  detail?: ReactNode
  tone?: Tone
  icon?: ReactNode
  compact?: boolean
}) {
  return (
    <div className={`mim-kpi mim-kpi-${tone}${compact ? ' mim-kpi-compact' : ''}`}>
      <div>
        <div className="mim-kpi-label">{label}</div>
        <div className="mim-kpi-value">{value}</div>
        {detail && <div className="mim-kpi-detail">{detail}</div>}
      </div>
      {icon && <div className="mim-kpi-icon">{icon}</div>}
    </div>
  )
}

export function StatusPill({
  status,
  tone,
}: {
  status: string
  tone?: 'pass' | 'warning' | 'critical' | 'info' | 'pending' | 'neutral'
}) {
  const normalized = status.toLowerCase()
  const resolved =
    tone ??
    (normalized.includes('critical') || normalized.includes('high') || normalized.includes('fail')
      ? 'critical'
      : normalized.includes('warn') || normalized.includes('medium') || normalized.includes('exception')
        ? 'warning'
        : normalized.includes('pass') || normalized.includes('complete') || normalized.includes('verified') || normalized.includes('success') || normalized.includes('resolved')
          ? 'pass'
          : normalized.includes('pending') || normalized.includes('progress') || normalized.includes('open')
            ? 'pending'
            : 'neutral')

  const icon =
    resolved === 'critical' ? <XCircle size={12} /> :
    resolved === 'warning' ? <AlertTriangle size={12} /> :
    resolved === 'pass' ? <CheckCircle2 size={12} /> :
    resolved === 'pending' ? <Clock size={12} /> :
    <Info size={12} />

  return <span className={`mim-pill mim-pill-${resolved}`}>{icon}{status}</span>
}

export function FilterPill({
  active,
  children,
  onClick,
  tone = 'blue',
}: {
  active?: boolean
  children: ReactNode
  onClick?: () => void
  tone?: 'blue' | 'gold'
}) {
  return (
    <button
      type="button"
      className={`mim-filter-pill ${active ? `active ${tone}` : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

export function EmptyState({ icon, title, detail }: { icon?: ReactNode; title: string; detail?: string }) {
  return (
    <div className="mim-empty">
      {icon && <div className="mim-empty-icon">{icon}</div>}
      <strong>{title}</strong>
      {detail && <span>{detail}</span>}
    </div>
  )
}

export function FieldList({
  rows,
}: {
  rows: Array<{ label: string; value?: ReactNode; flagged?: boolean }>
}) {
  return (
    <div className="mim-field-list">
      {rows.map((row) => (
        <div key={row.label} className="mim-field-row">
          <span>{row.label}</span>
          <strong className={row.flagged ? 'flagged' : ''}>{row.value ?? '-'}</strong>
        </div>
      ))}
    </div>
  )
}

export function formatDateTime(value?: string) {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

export function relativeAge(value?: string) {
  if (!value) return 'Never'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const seconds = Math.max(1, Math.round((Date.now() - date.getTime()) / 1000))
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}
